import type { SyncChange } from "./sync";

export const SYNC_BUNDLE_VERSION = 1 as const;

type SyncBundleBody = {
  kind: "browser-local-sync-bundle";
  bundleVersion: typeof SYNC_BUNDLE_VERSION;
  createdAt: string;
  changes: readonly SyncChange[];
};

export type SyncBundle = SyncBundleBody & { checksum: string };

export class SyncBundleError extends Error {
  constructor(public readonly code: "invalid_bundle" | "unsupported_bundle", message: string) {
    super(message);
    this.name = "SyncBundleError";
  }
}

export async function exportSyncBundle(changes: readonly SyncChange[]): Promise<Uint8Array> {
  const normalized = dedupeChanges(changes);
  normalized.forEach(validateChange);
  const body: SyncBundleBody = {
    kind: "browser-local-sync-bundle",
    bundleVersion: SYNC_BUNDLE_VERSION,
    createdAt: new Date().toISOString(),
    changes: normalized,
  };
  return new TextEncoder().encode(JSON.stringify({ ...body, checksum: await checksum(body) } satisfies SyncBundle));
}

export async function importSyncBundle(bytes: Uint8Array): Promise<SyncBundle> {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new SyncBundleError("invalid_bundle", "Sync bundle is not valid JSON.");
  }
  if (!value || typeof value !== "object") throw new SyncBundleError("invalid_bundle", "Sync bundle must be an object.");
  const candidate = value as Partial<SyncBundle>;
  if (candidate.kind !== "browser-local-sync-bundle" || candidate.bundleVersion !== SYNC_BUNDLE_VERSION) {
    throw new SyncBundleError("unsupported_bundle", "Sync bundle version is not supported.");
  }
  if (!Array.isArray(candidate.changes) || typeof candidate.createdAt !== "string" || typeof candidate.checksum !== "string") {
    throw new SyncBundleError("invalid_bundle", "Sync bundle envelope is incomplete.");
  }
  candidate.changes.forEach(validateChange);
  const body: SyncBundleBody = {
    kind: candidate.kind,
    bundleVersion: candidate.bundleVersion,
    createdAt: candidate.createdAt,
    changes: dedupeChanges(candidate.changes),
  };
  if (candidate.checksum !== await checksum(body)) {
    throw new SyncBundleError("invalid_bundle", "Sync bundle checksum does not match.");
  }
  return { ...candidate, changes: body.changes } as SyncBundle;
}

function dedupeChanges(changes: readonly SyncChange[]): SyncChange[] {
  const seen = new Set<string>();
  return changes.filter((change) => {
    if (seen.has(change.changeId)) return false;
    seen.add(change.changeId);
    return true;
  }).map((change) => structuredClone(change));
}

function validateChange(change: SyncChange): void {
  if (
    !change ||
    typeof change.changeId !== "string" ||
    typeof change.appId !== "string" ||
    typeof change.resource !== "string" ||
    typeof change.entityId !== "string" ||
    (change.operation !== "upsert" && change.operation !== "delete") ||
    !Number.isInteger(change.version) ||
    change.version < 1 ||
    typeof change.occurredAt !== "string"
  ) {
    throw new SyncBundleError("invalid_bundle", "Sync bundle contains an invalid change.");
  }
}

async function checksum(value: SyncBundleBody): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(value)));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
